import { CloudUpload, FileText, History } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useCreateHistoricalPayroll } from '../../api'
import { fileSize } from '../../lib/fileExport'
import { Field, ModalFooter } from './Field'
import { apiErrorMessage } from '@/i18n'

/** İçinde bulunulan aydan önceki son dönem (YYYY-MM); geçmiş bordro yalnız bu ve öncesi için girilir. */
function lastClosedPeriod() {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() - 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const MAX_FILE = 15 * 1024 * 1024

/**
 * Sistem öncesi dönemden (zaten ödenmiş) bordro aktarımı. Tutarlar muhasebe bordrosundan aynen girilir; sistem
 * yeniden hesaplamaz, ödeme talebi ve onay üretmez. Eklenen belge bordro PDF'i olarak indirilir.
 */
export function GecmisBordroModal({ personelId, onClose }: { personelId: string; onClose: () => void }) {
  const { t } = useTranslation()
  const create = useCreateHistoricalPayroll()
  const maxPeriod = lastClosedPeriod()
  const [period, setPeriod] = useState(maxPeriod)
  const [gross, setGross] = useState('')
  const [net, setNet] = useState('')
  const [sgk, setSgk] = useState('')
  const [incomeTax, setIncomeTax] = useState('')
  const [stampTax, setStampTax] = useState('')
  const [bonus, setBonus] = useState('')
  const [workedDays, setWorkedDays] = useState('30')
  const [note, setNote] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const req = (v: string) => (submitted && !v ? t('hr.personel.actions.required') : undefined)
  const errPeriod = submitted && (!period || period > maxPeriod) ? t('hr.personel.gecmisBordro.periodPast') : undefined
  const errNet = req(net) ?? (submitted && Number(net) > Number(gross || 0) + Number(bonus || 0) ? t('hr.personel.gecmisBordro.netTooHigh') : undefined)
  const errFile = file && file.size > MAX_FILE ? t('hr.personel.gecmisBordro.fileTooBig') : undefined

  const submit = () => {
    setSubmitted(true)
    if (!period || period > maxPeriod || !gross || !net || Number(net) > Number(gross) + Number(bonus || 0) || errFile) return
    create.mutate(
      { employeeId: personelId, period, gross, net, sgk, incomeTax, stampTax, bonus, workedDays, note, file },
      {
        onSuccess: () => {
          toast.success(t('hr.personel.gecmisBordro.saved', { period }))
          onClose()
        },
        onError: (error) => toast.error(apiErrorMessage(error)),
      },
    )
  }

  const money = (id: string, label: string, value: string, set: (v: string) => void, error?: string) => (
    <Field id={id} label={label} error={error}>
      <NumberInput id={id} className="h-10 rounded-xl" value={value} onChange={set} maxDecimals={2} minDecimalsOnBlur={2} suffix="TRY" aria-invalid={!!error} />
    </Field>
  )

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      icon={<History />}
      title={t('hr.personel.gecmisBordro.title')}
      description={t('hr.personel.gecmisBordro.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={<ModalFooter onCancel={onClose} submitLabel={t('hr.personel.gecmisBordro.save')} />}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="gb-donem" label={t('hr.personel.gecmisBordro.period')} error={errPeriod}>
          <Input id="gb-donem" type="month" className="h-10 rounded-xl" value={period} max={maxPeriod} onChange={(e) => setPeriod(e.target.value)} aria-invalid={!!errPeriod} />
        </Field>
        <Field id="gb-gun" label={t('hr.personel.gecmisBordro.workedDays')}>
          <Input id="gb-gun" type="number" min={0} max={31} className="h-10 rounded-xl" value={workedDays} onChange={(e) => setWorkedDays(e.target.value)} />
        </Field>
        {money('gb-brut', t('hr.personel.gecmisBordro.gross'), gross, setGross, req(gross))}
        {money('gb-ek', t('hr.personel.gecmisBordro.bonus'), bonus, setBonus)}
        {money('gb-sgk', t('hr.personel.gecmisBordro.sgk'), sgk, setSgk)}
        {money('gb-gv', t('hr.personel.gecmisBordro.incomeTax'), incomeTax, setIncomeTax)}
        {money('gb-dv', t('hr.personel.gecmisBordro.stampTax'), stampTax, setStampTax)}
        {money('gb-net', t('hr.personel.gecmisBordro.net'), net, setNet, errNet)}
        <Field id="gb-file" label={t('hr.personel.gecmisBordro.file')} error={errFile} className="sm:col-span-2">
          <label
            htmlFor="gb-file"
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-xl border border-dashed bg-muted/30 p-4 text-sm transition-colors hover:bg-muted/60',
              errFile ? 'border-destructive/60' : 'border-border',
              file && !errFile && 'border-success/40 bg-success/5',
            )}
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-card text-muted-foreground shadow-xs">
              {file ? <FileText className="size-4 text-success" /> : <CloudUpload className="size-4" />}
            </span>
            <span className="min-w-0">
              <span className="block truncate font-medium">{file ? file.name : t('hr.personel.actions.belge.dosyaSec')}</span>
              <span className="block text-xs text-muted-foreground">{file ? fileSize(file.size) : 'PDF, JPG, PNG, WEBP · 15 MB'}</span>
            </span>
          </label>
          <input
            id="gb-file"
            type="file"
            className="sr-only"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        <Field id="gb-not" label={t('hr.personel.gecmisBordro.note')} className="sm:col-span-2">
          <Textarea id="gb-not" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
